import { memo, useRef, forwardRef } from 'react';

import { Collapsible, CollapsibleProps } from '../Collapsible';
import { Input, InputProps } from '../../designSystem/Input';
import { iconButtonVariants } from '../../designSystem/IconButton';
import { cn } from '../../lib/styles';
import { useMergedRefs } from '@page/core/hooks/common';

type CollapsibleInputProps = CollapsibleProps &
  Omit<InputProps, 'placeholder'> & {
    placeholder?: string;
    iconOnly?: boolean;
    inputClassName?: string;
  };

const CollapsibleInputBase = (
  {
    placeholder,
    iconOnly = false,
    alwaysExpanded = false,
    value = '',
    onChange,
    onClear,
    className,
    inputClassName,
    collapsedClassName = 'w-34',
    expandedClassName = 'w-64',
    leftIcon,
    leftIconClassName,
    ...rest
  }: CollapsibleInputProps,
  forwardedRef: React.ForwardedRef<HTMLInputElement>,
) => {
  const internalRef = useRef<HTMLInputElement | null>(null);
  const ref = useMergedRefs(forwardedRef, internalRef);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(event);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      internalRef.current?.blur();
    }
  };

  const handleClear = () => {
    internalRef.current?.blur();
    onClear?.();
  };

  const isIconOnly = iconOnly || (leftIcon && !placeholder);

  return (
    <Collapsible
      alwaysExpanded={!!value || alwaysExpanded}
      collapsedClassName={collapsedClassName}
      expandedClassName={expandedClassName}
      className={className}
    >
      {({ isExpanded }) => {
        const isIconCompact = isIconOnly && !isExpanded;

        return (
          <Input
            ref={ref}
            type="text"
            placeholder={iconOnly && !isExpanded ? '' : placeholder}
            value={value}
            onChange={handleInputChange}
            size="sm"
            className={cn(
              'font-body-sm font-medium',
              'hover:border-transparent',
              {
                [cn('px-2 outline-none', iconButtonVariants({ variant: 'tertiary', color: 'default' }))]: isIconCompact,
                'cursor-pointer': !isExpanded,
              },
              inputClassName,
            )}
            leftIconClassName={cn(
              {
                'left-1.5': isIconCompact,
              },
              'pointer-events-none',
              leftIconClassName,
            )}
            onKeyDown={handleKeyDown}
            clearable
            forceShowClearButton={isExpanded}
            onClear={handleClear}
            leftIcon={leftIcon}
            {...rest}
          />
        );
      }}
    </Collapsible>
  );
};

export const CollapsibleInput = memo(forwardRef(CollapsibleInputBase));
